import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Card,
  Grid,
  Box,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  FindInPage as FindInPageIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  DocumentScanner as DocumentScannerIcon,
} from '@mui/icons-material';
import {
  GridRowModes,
  DataGridPro,
  GridActionsCellItem,
  koKR,
  GridCellModes,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { toFilePreviewUrl } from '../../../config';
import {
  CaretIntentProvider,
  markClickOffset,
  markSelectAll,
  renderComparisonTextEditCell,
} from '../../GTestComparison/comparisonEditCaret';
import * as gclientG04docuGTestMapActions from "../../../store/gclientG04docuGTestMap";
import * as gtestComparisonActions from "../../../store/gtestComparison";
import * as subMatFolderActions from "../../../store/subMatFolder";
import * as gprojectActions from "../../../store/gproject";
import {
  CustomNoRowsOverlay,
} from "../../../components/datagrid";
import { v4 as uuidv4 } from 'uuid';
import { DialogTitleClose } from "../../dialog";
import GlassUtils from "../../../utils/GlassUtils";

const TestComparisonDialog = ({
  gprojectId,
  open,
  onClose,
  selectedRow,
  fullScreen = false,
  onSaveSuccess,
}) => {
  const dispatch = useDispatch();
  
  // 시험성과대비표 관련 state
  const [rowsG04docuGTestComparison, setRowsG04docuGTestComparison] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [cellModesModel, setCellModesModel] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // 수정 모드 여부
  const [originalData, setOriginalData] = useState([]); // 원본 데이터 저장
  const [hasChanges, setHasChanges] = useState(false); // 데이터 변경 여부
  const apiRef = useGridApiRef();
  const caretIntentRef = useRef(null);
  
  const withIds = (list) => (list || []).map((row) => ({
    ...row,
    id: row.id || uuidv4(),
  }));

  const buildLeadJson = (row) => {
    const classification = row?.classification || 'no_classification';
    let thickness = 0;
    if (row?.division === 'RAW_MATERIAL') {
      const parsed = Number(row.code);
      thickness = Number.isFinite(parsed) ? parsed : 0;
    }
    const classificationFt = row?.classificationFt || '';
    return {
      thickness,
      classification,
      ft_thick_class: thickness && classificationFt ? `${thickness}_${classificationFt}` : '',
      ks2004_thickness: '',
    };
  };

  const resolveAnalysisClientId = (row) => (
    row?.analysisGclientId || row?.gclient?.id || row?.gclientId
  );

  const isQrExtractType = (extractType) => {
    const type = String(extractType || '').toUpperCase();
    return type === 'QR' || type === 'QRCODE' || type.includes('QR');
  };

  const qrRowsFromResult = (result) => {
    if (!isQrExtractType(result?.extractType)) return [];
    const list = result?.g04docuGTestComparisons || [];
    return list.length ? withIds(list) : [];
  };

  // 지금 보고 있는 성적서에서 QR 행만 가져온다. QR이 아니면 빈 목록이다.
  const readQrRows = async () => {
    const lead = buildLeadJson(selectedRow);
    const substituteFolderId = selectedRow?.substituteFolderId;
    const substituteSelected = !!(
      selectedRow?.currentDocumentPath
      && selectedRow?.documentPath
      && selectedRow.currentDocumentPath !== selectedRow.documentPath
    );
    const folderId = substituteFolderId
      || (!substituteSelected && selectedRow?.folderSource ? selectedRow?.productFolderId : null);

    if (folderId) {
      try {
        const result = await dispatch(subMatFolderActions.extractTestComparison(folderId, {
          gstandardId: selectedRow?.gstandardId || null,
          jsonData: lead,
        }));
        return qrRowsFromResult(result);
      } catch (error) {
        console.error('폴더 성적서 QR 분석 실패:', error);
        return [];
      }
    }

    const g04docuGTestId = selectedRow?.g04docuGTestId;
    const gclientId = resolveAnalysisClientId(selectedRow);
    if (gclientId && g04docuGTestId) {
      try {
        const result = await gclientG04docuGTestMapActions.selectResultsFromGTestDocumentDirect(
          gclientId,
          g04docuGTestId,
          lead,
        );
        return qrRowsFromResult(result);
      } catch (error) {
        console.error('성적서 QR 분석 실패:', error);
      }
    }
    return [];
  };

  // 인증규격에 등록된 표준 항목. 두께·등급으로 기준 문장을 좁힌다.
  const loadStandardTemplate = async () => {
    const certificationId = selectedRow?.g04docuGCertificationId;
    const gstandardId = selectedRow?.gstandardId;
    let masters = [];
    try {
      if (certificationId) {
        masters = await gtestComparisonActions.selectAllByByG04docuGCertificationsDirect(certificationId) || [];
      } else if (gstandardId) {
        masters = await gtestComparisonActions.selectAllByGstandardIdDirect(gstandardId) || [];
      }
    } catch (error) {
      console.error('인증규격 템플릿 조회 실패:', error);
      return [];
    }

    const { code, division, classification, gstandardNo, sample, g04docuGTestId } = selectedRow || {};
    return masters.map((row) => ({
      ...row,
      id: uuidv4(),
      g04docuGTestId: g04docuGTestId || null,
      criterion: row.criterion
        ? GlassUtils.selectCriterion(row.criterion, code, classification, division, sample, gstandardNo)
        : row.criterion,
    }));
  };

  // 시험성과대비표 그리드 컬럼 정의
  const columnsGTestComparison = [
    {
      field: 'item',
      headerName: '구분',
      editable: (params) => !params.row.office,
      width: 160,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'unit',
      headerName: '단위',
      editable: (params) => !params.row.office,
      width: 100,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'criterion',
      headerName: '기준',
      editable: true,
      width: 300,
      renderEditCell: renderComparisonTextEditCell,
      renderCell: (params) => {
        if (params.row.noDetailCriterion) {
          return (
            <Tooltip title={params.row.noDetailCriterion}>
              <span style={{ color: '#DB4E2E', fontWeight: 'bold' }}>{params.value}</span>
            </Tooltip>
          );
        }
        return params.value;
      }
    },
    {
      field: 'value',
      headerName: '시험결과',
      editable: (params) => !params.row.office,
      width: 120,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'result',
      headerName: '판정',
      width: 120,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['합격', '불합격', '참조값'],
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.value === '합격') {
          return <span style={{ color: '#1976d2', fontWeight: 'bold' }}>합격</span>;
        }
        if (params.value === '불합격') {
          return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>불합격</span>;
        }
        return params.value;
      }
    },
    {
      field: 'method',
      headerName: '시험방법',
      editable: true,
      width: 120,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'office',
      headerName: '시험기관',
      hide: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '기능',
      width: 87,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        // office가 있으면 빈 배열 반환하여 아이콘 숨김
        if (row.office) {
          return [];
        }
        return [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  // 시험성과대비표 데이터 로드 - 초기 로딩 모드와 수정 모드 구분
  useEffect(() => {
    if (!open) return;
    if (!selectedRow) {
      setLoaded(true);
      return;
    }
    loadTestComparisonData();
  }, [open, selectedRow]);

  const applyRows = (rows, { editMode, changed }) => {
    setIsEditMode(editMode);
    setRowsG04docuGTestComparison(rows);
    setOriginalData(rows);
    setHasChanges(changed);
  };

  // 대체 JSON → QR → 인증규격 템플릿. 서류등록에 저장된 원본 행은 쓰지 않는다.
  const loadTestComparisonData = async () => {
    try {
      setLoaded(false);
      if (gprojectId && selectedRow?.g04docuGTestId) {
        const projectData = await gprojectActions.selectGProjectGTestComparisonsDirect(gprojectId, selectedRow.g04docuGTestId);
        const savedRows = projectData?.[0]?.gtestComparisonData || projectData?.[0]?.gTestComparisonData;
        if (projectData && projectData.length > 0 && savedRows?.length) {
          applyRows(withIds(savedRows), { editMode: true, changed: false });
          return;
        }
      }
      const qrRows = await readQrRows();
      if (qrRows.length > 0) {
        applyRows(qrRows, { editMode: false, changed: true });
        return;
      }
      const templateRows = await loadStandardTemplate();
      applyRows(templateRows, { editMode: false, changed: templateRows.length > 0 });
    } catch (error) {
      console.error('시험성과대비표 데이터 로드 실패:', error);
      applyRows([], { editMode: false, changed: false });
    } finally {
      setLoaded(true);
    }
  };

  const handleAnalyze = async () => {
    if (rowsG04docuGTestComparison.length > 0) {
      const proceed = window.confirm('현재 목록을 지우고, 지금 성적서의 QR을 다시 읽습니다. QR이 없으면 인증규격 항목으로 채웁니다. 계속할까요?');
      if (!proceed) return;
    }
    setLoaded(false);
    try {
      const qrRows = await readQrRows();
      if (qrRows.length > 0) {
        setRowsG04docuGTestComparison(qrRows);
        setHasChanges(true);
        return;
      }
      const templateRows = await loadStandardTemplate();
      if (!templateRows.length) {
        alert('QR을 읽지 못했고, 인증규격 항목도 없습니다. 성적서와 표준을 확인해 주세요.');
        return;
      }
      setRowsG04docuGTestComparison(templateRows);
      setHasChanges(true);
    } catch (error) {
      console.error('성적서 분석 실패:', error);
      alert('성적서 분석에 실패했습니다.');
    } finally {
      setLoaded(true);
    }
  };

  // 시험성과대비표 관련 핸들러 함수들
  const handleClickAdd = () => {
    const id = uuidv4();
    
    setRowsG04docuGTestComparison((oldRows) => [...oldRows, { id, item: '시험항목', unit: '단위', criterion: '기준', value: '', result: '', isNew: true }]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.View, fieldToFocus: 'item' },
    }));
    setHasChanges(true); // 무조건 변경 감지
  };

  const handleDeleteClick = (id) => () => {
    setRowsG04docuGTestComparison(prevRows => prevRows.filter(row => row.id !== id));
    
    // 수정 모드에서 행 삭제 시 변경 감지
    if (isEditMode) {
      setHasChanges(true);
    }
  };

  const handleCellEditCommit = (params) => {
    const { id, field, value } = params;
    setRowsG04docuGTestComparison(prevRows => 
      prevRows.map(row => 
        row.id === id ? { ...row, [field]: value } : row
      )
    );
    setHasChanges(true); // 무조건 변경 감지
  };

  // 데이터 변경 여부 확인
  const checkForChanges = (currentRows) => {
    if (originalData.length !== currentRows.length) {
      setHasChanges(true);
      return;
    }
    
    const hasDataChanged = currentRows.some((currentRow, index) => {
      const originalRow = originalData[index];
      if (!originalRow) return true;
      
      return Object.keys(currentRow).some(key => {
        if (key === 'id') return false; // id는 비교에서 제외
        return currentRow[key] !== originalRow[key];
      });
    });
    
    setHasChanges(hasDataChanged);
  };

  const handleClickG04docuGTestComparison = async () => {
    // console.log({ gprojectId,selectedRow, rowsG04docuGTestComparison });    
    // return;

    try {
      if (isEditMode) {
        // 수정 모드: 기존 데이터 수정
        await gprojectActions.modifyGProjectGTestComparisonDirect({
          gprojectId: gprojectId,
          g04docuGTestId: selectedRow.g04docuGTestId,
          gTestComparisonData: rowsG04docuGTestComparison,
          useDoc: true,
          classification: selectedRow.classification,
        });
        alert('시험성과대비표가 수정되었습니다.');
        // 수정 성공 후 변경 사항 리셋
        setHasChanges(false);
        setOriginalData([...rowsG04docuGTestComparison]);
      } else {
        // 초기 로딩 모드: 새로 생성
        await gprojectActions.createGProjectGTestComparisonDirect({
          gprojectId: gprojectId,
          g04docuGTestId: selectedRow.g04docuGTestId,
          gTestComparisonData: rowsG04docuGTestComparison,
          useDoc: true,
          classification: selectedRow.classification,
        });
        alert('시험성과대비표가 저장되었습니다.');
        // 저장 후 수정 모드로 변경
        setIsEditMode(true);
        setHasChanges(false);
        setOriginalData([...rowsG04docuGTestComparison]);
      }
      
      // 저장/수정 성공 시 콜백 호출
      if (onSaveSuccess) {
        onSaveSuccess(selectedRow);
      }
    } catch (error) {
      console.error('시험성과대비표 저장/수정 실패:', error);
      alert('시험성과대비표 저장/수정에 실패했습니다.');
    }
  };

  const handleClickSearchG04docuGTestComparison = () => {
    if (originalData.length > 0) {
      setRowsG04docuGTestComparison(JSON.parse(JSON.stringify(originalData)));
      setHasChanges(false); // 변경사항 초기화
      alert('수정한 내용이 원래의 상태로 되돌려졌습니다.');
    } else {
      alert('되돌릴 원본 데이터가 없습니다.');
    }
  };

  const handleClickInitG04docuGTestComparison = () => {
    // 규격으로 새로고침 기능 (추후 구현)
    alert('규격으로 새로고침 기능은 추후 구현 예정입니다.');
  };

  // DataGridPro 관련 함수들
  const handleCellModesModelChange = (newModel) => {
    setCellModesModel(newModel);
  };

  const handleCellKeyDown = (params, event) => {
    if (
      params.cellMode !== 'edit'
      && params.isEditable
      && event.key === ' '
      && !event.shiftKey
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
    ) {
      event.defaultMuiPrevented = true;
      event.preventDefault();
      markSelectAll(caretIntentRef, params.id, params.field);
      apiRef.current.setCellMode(params.id, params.field, 'edit');
      return;
    }
    if (params.cellMode !== 'edit' && params.isEditable && event.key === 'Enter') {
      markSelectAll(caretIntentRef, params.id, params.field);
    }
    // 보기 상태의 글자 키는 편집을 열지 않는다. 입력칸은 엔터나 스페이스로 만든 뒤에만 친다.
    if (
      params.cellMode !== 'edit'
      && params.isEditable
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
      && event.key.length === 1
    ) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleCellClick = (params, event) => {
    if (!params.isEditable || params.cellMode === 'edit') return;
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) return;
    const focusedCell = apiRef.current.state?.focus?.cell;
    if (!focusedCell || focusedCell.id !== params.id || focusedCell.field !== params.field) return;
    markClickOffset(caretIntentRef, params.id, params.field, event.clientX, event.clientY);
    apiRef.current.setCellMode(params.id, params.field, 'edit');
  };

  const handleCellDoubleClick = (params, event) => {
    if (!params.isEditable || params.cellMode === 'edit') return;
    markClickOffset(caretIntentRef, params.id, params.field, event.clientX, event.clientY);
  };

  const processRowUpdate = (newRow, oldRow) => {
    setRowsG04docuGTestComparison(prevRows => 
      prevRows.map(row => row.id === newRow.id ? newRow : row)
    );
    return newRow;
  };

  const handleClose = () => {
    onClose && onClose();
    setRowsG04docuGTestComparison([]);
    setOriginalData([]);
    setLoaded(false);
    setIsEditMode(false);
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      disableEscapeKeyDown
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100vh' : '90vh',
          maxHeight: fullScreen ? '100vh' : '90vh'
        }
      }}
    >
      <DialogTitleClose
        onClose={handleClose}
        fullScreen={fullScreen}
        sx={{
          backgroundColor: fullScreen ? "#1976d2" : "transparent",
          color: fullScreen ? "white" : "inherit",
          cursor: fullScreen ? '' : 'move',
        }}
      >
        <Typography variant="h6">
          시험성과대비표 {isEditMode ? '수정' : '작성'}
        </Typography>
        <Typography variant="body2" color={fullScreen ? "white" : "text.secondary"}>
          {selectedRow?.company} - {selectedRow?.productName}
        </Typography>
      </DialogTitleClose>
      
      <DialogContent sx={{ p: 0 }}>
        <Card sx={{ m: 3, p: 2 }}>
          <PanelGroup direction="horizontal" style={{ height: '750px' }}>
            {/* 성적서 패널 */}
            <Panel defaultSize={40} minSize={20} maxSize={80}>
              <Box sx={{ pr: 2, height: '100%' }}>
                <Grid container>
                  <Grid item xs={12} display="flex" alignItems="center">
                    <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>{"∙ 성적서"}</Typography>
                  </Grid>
                </Grid>
                <div style={{ height: 700, width: '100%', marginTop: '8px' }}>
                  {(selectedRow?.currentDocumentPath || selectedRow?.documentPath) ? (
                    <iframe 
                      src={toFilePreviewUrl(selectedRow.currentDocumentPath || selectedRow.documentPath)} 
                      width="100%" 
                      height="100%"
                    />
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      border: '1px solid #ddd',
                      borderRadius: 1
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        성적서가 없습니다.
                      </Typography>
                    </Box>
                  )}
                </div>
              </Box>
            </Panel>

            {/* 스플릿바 */}
            <PanelResizeHandle
              style={{
                width: '8px',
                backgroundColor: '#e0e0e0',
                cursor: 'col-resize',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#9e9e9e';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#e0e0e0';
              }}
            />

            {/* 시험성과대비표 패널 */}
            <Panel defaultSize={60} minSize={20} maxSize={80}>
              <Box sx={{ pl: 2, height: '100%' }}>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={12} md={3} lg={2} display="flex" justifyContent="flex-start" alignItems="center">
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        ml: 1, 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {"∙ 시험성과대비표"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={9} lg={10} display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
                    <Tooltip title="목록의 마지막에 시험항목 한 행을 추가합니다.">
                      <Button 
                        color="primary" 
                        startIcon={<AddIcon />}
                        onClick={handleClickAdd}
                        size="small"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: { xs: 'auto', sm: 'auto' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {"추가"}
                      </Button>
                    </Tooltip>
                    <Tooltip title="시험결과를 저장하고 시험성과대비표(PDF)를 작성합니다.">
                      <Button 
                        color="primary" 
                        startIcon={<SaveIcon />}
                        onClick={handleClickG04docuGTestComparison}
                        disabled={isEditMode && !hasChanges}
                        size="small"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: { xs: 'auto', sm: 'auto' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {isEditMode ? "수정" : "저장"}
                      </Button>
                    </Tooltip>
                    <Tooltip title="지금 성적서에서 QR을 읽고, QR이 없으면 인증규격 항목으로 목록을 채웁니다.">
                      <Button
                        color="primary"
                        startIcon={<DocumentScannerIcon />}
                        onClick={handleAnalyze}
                        size="small"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: { xs: 'auto', sm: 'auto' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {"성적서 분석"}
                      </Button>
                    </Tooltip>
                    <Tooltip title="수정하신 내용을 취소하고 원래의 상태로 되돌립니다.">
                      <Button 
                        color="primary" 
                        startIcon={<UndoIcon />}
                        onClick={handleClickSearchG04docuGTestComparison}
                        disabled={!hasChanges}
                        size="small"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: { xs: 'auto', sm: 'auto' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {"작업취소"}
                      </Button>
                    </Tooltip>
                    {/* <Tooltip title="규격으로 새로고침 기능은 추후 구현 예정입니다.">
                      <Button 
                        color="primary" 
                        startIcon={<RefreshIcon />}
                        onClick={handleClickInitG04docuGTestComparison}
                        size="small"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: { xs: 'auto', sm: 'auto' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {"새로고침"}
                      </Button>
                    </Tooltip> */}
                  </Grid>
                </Grid>
                {/* 시험성과대비표 그리드 화면출력 */}
                <div style={{ height: 700, width: '100%', marginTop: '8px' }}>
                  <CaretIntentProvider value={caretIntentRef}>
                  <DataGridPro
                    localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                    getCellClassName={(params) => {
                      // office 속성이 있고, criterion, result, method 필드가 아닌 경우에만 disabled-row 클래스 적용
                      if (params.row.office && 
                          !['criterion', 'result', 'method'].includes(params.field)) {
                        return 'disabled-row';
                      }
                      return '';
                    }}
                    sx={{ 
                      cursor: 'pointer', 
                      fontSize: '0.85em',
                      // office 속성이 있는 행에 대한 스타일 추가
                      '& .disabled-row': {
                        '&:hover': {
                          backgroundColor: '#eeeeee',
                        },
                        opacity: 0.85,
                        cursor: 'not-allowed',
                      }
                    }}
                    slots={{
                      noRowsOverlay: CustomNoRowsOverlay,
                      loadingOverlay: LinearProgress,
                    }}
                    headerHeight={38}
                    rowHeight={34}
                    rows={rowsG04docuGTestComparison}
                    columns={columnsGTestComparison}
                    loading={!loaded}
                    disableColumnMenu
                    rowReordering
                    editMode="cell"
                    isCellEditable={(params) => {
                      // criterion, result, method 필드는 항상 편집 가능
                      if (params.field === 'criterion' || params.field === 'result' || params.field === 'method') {
                        return true;
                      }
                      // 나머지 필드는 office 속성이 없을 때만 편집 가능
                      return !params.row.office;
                    }}
                    apiRef={apiRef}
                    onCellKeyDown={handleCellKeyDown}
                    onCellEditCommit={handleCellEditCommit}
                    onCellModesModelChange={handleCellModesModelChange}
                    onCellClick={handleCellClick}
                    onCellDoubleClick={handleCellDoubleClick}
                    processRowUpdate={processRowUpdate}
                    rowModesModel={rowModesModel}
                    cellModesModel={cellModesModel}
                  />
                  </CaretIntentProvider>
                </div>
              </Box>
            </Panel>
          </PanelGroup>
        </Card>
      </DialogContent>
      
      {/* 하단 닫기 버튼 제거 */}
    </Dialog>
  );
};

export default TestComparisonDialog; 